import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import { BarChartIcon, BankNoteIcon, CalendarIcon, HomeIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { useWorkItemStatusCounts } from '../../hooks/queries/useWorkItemStatusCounts';
import { useFeatureGate } from '../../hooks/useFeatureGates';
import { PATHS } from '../../paths';
import { useAuthState } from '../../providers/AuthProvider';
import { usePathWithDateRange } from '../../providers/DateRangeProvider';
import { useSidePanel } from '../../providers/SidePanelProvider';
import EntitySearchModal from '../search/EntitySearchModal';

import { MAIN_SIDEBAR_WIDTH_PX } from './dimensions';
import FullSidebarContent from './FullSidebarContent';
import IconRailSidebar from './IconRailSidebar';
import MobileNavDrawer from './MobileNavDrawer';
import MobileTopBar from './MobileTopBar';

import type { NavItemType } from './config';
import type { LoggedInUser } from '../../providers/AuthProvider';

const ALERT_COUNT_POLL_INTERVAL_MS = 60_000;

interface IBaseSidebarProps {
  user: LoggedInUser;
  /** URL of the currently active item. */
  activeUrl?: string;
  /** List of items to display. */
  items: NavItemType[];
  /** List of footer items to display. */
  footerItems?: NavItemType[];
  /** Whether to show the account card. */
  showAccountCard?: boolean;
  /**
   * Whether to render the "Ask Mantle" footer button (full sidebar +
   * mobile drawer) and its compact icon-rail variant. Drives visibility
   * across all three sidebar presentations together.
   */
  showAssistantButton?: boolean;
  /**
   * Whether to render the alerts bell. Drives visibility across all three
   * sidebar presentations and the mobile top bar together.
   */
  showAlertsBell?: boolean;
  /** Unread alert count shown on the bell's pip. */
  alertsUnreadCount?: number;
  /**
   * Whether the Alerts Center is showing in the `xl+` side panel. The route
   * redirects away once the panel takes over, so the pathname alone would
   * never mark the bell current.
   */
  isAlertsPanelOpen?: boolean;
  /**
   * Opens the center in the side panel instead of navigating. Supplied only at
   * `xl+`; see {@link NotificationBellButton}.
   */
  onOpenAlertsInPanel?: () => void;
  /** Additional CSS classes to apply to the desktop fixed sidebar. */
  className?: string;
}

/**
 * Responsive navigation chrome for the logged-in app. Renders three
 * presentations driven by Tailwind visibility classes (no viewport JS):
 *
 * - `lg` and up: 260px fixed sidebar on the left (full content).
 * - `md`: 64px icon-only rail on the left.
 * - below `md`: sidebar hidden; fixed top bar hosts a hamburger that opens a
 *   left-slide drawer containing the full sidebar content.
 *
 * Owns search-modal (Cmd/Ctrl+K) and drawer open state; closes the drawer on
 * route change.
 *
 * **Why CSS visibility instead of `useBreakpoint` JS gating?**
 * All three trees mount at every viewport and Tailwind's `hidden md:flex
 * lg:hidden`-style classes hide the inactive ones. Costs ~30–40 hidden DOM
 * nodes (`display:none`, not painted, not in layout) — well below anything
 * worth optimizing. In return:
 * - State is preserved across resize for most subtrees (e.g. the search
 *   modal). JS gating would unmount the inactive variants and require
 *   `useEffect`s to reconcile stale state against unmounted views.
 * - First paint is correct without a window-size read. There's no SSR in
 *   this app, so flash isn't the threat — the savings are robustness, not
 *   pixels.
 * If we ever embed something expensive (charts, virtualized lists) inside
 * one of the variants, revisit and gate that subtree on `useBreakpoint`.
 * The shell stays cheap.
 *
 * **Drawer is the one exception** to "state preserved across resize":
 * `MobileNavDrawer` is `lg:hidden` (visible at both `<md` and `md`) — the
 * mobile top bar's hamburger opens it at `<md`, and the icon rail's top
 * hamburger opens it at `md`. At `lg+` the drawer's overlay is
 * CSS-invisible but `react-aria-components`' `Modal` still mounts the
 * dialog and activates a focus trap when `isOpen` is true. The hamburgers
 * that open it are both also gone at `lg+` so a fresh open at `lg+` is
 * impossible, but stale `isDrawerOpen=true` across the resize boundary
 * is not. We watch the `lg` breakpoint and force-close when it crosses
 * true.
 */
export const BaseSidebar = ({
  user,
  activeUrl,
  items,
  footerItems = [],
  showAccountCard = true,
  showAssistantButton = false,
  showAlertsBell = false,
  alertsUnreadCount = 0,
  isAlertsPanelOpen = false,
  onOpenAlertsInPanel,
  className,
}: IBaseSidebarProps) => {
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const isLgUp = useBreakpoint('lg');

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  React.useEffect(() => {
    setIsDrawerOpen(false);
  }, [activeUrl]);

  // Defensive: close the drawer if the viewport crosses to `lg+` while it's
  // open. The overlay is `lg:hidden` (CSS-invisible at `lg+`) but the
  // underlying react-aria modal would still keyboard-trap focus. See JSDoc
  // above.
  React.useEffect(() => {
    if (isLgUp) {
      setIsDrawerOpen(false);
    }
  }, [isLgUp]);

  const openSearch = React.useCallback(() => setIsSearchOpen(true), []);
  const openDrawer = React.useCallback(() => setIsDrawerOpen(true), []);

  const alertsBellProps = {
    showAlertsBell,
    alertsUnreadCount,
    isAlertsCurrent: isAlertsPanelOpen || activeUrl === PATHS.ALERTS,
    onOpenAlertsInPanel,
  };

  return (
    <>
      {/* Desktop fixed sidebar (lg+) */}
      <div
        style={{ '--width': `${MAIN_SIDEBAR_WIDTH_PX}px` } as React.CSSProperties}
        className={cx(
          'lg:w-(--width) bg-app shadow-nav-edge fixed inset-y-0 left-0 z-30 hidden max-w-full lg:flex',
          className
        )}
      >
        <FullSidebarContent
          user={user}
          activeUrl={activeUrl}
          items={items}
          footerItems={footerItems}
          showAccountCard={showAccountCard}
          showAssistantButton={showAssistantButton}
          onOpenSearch={openSearch}
          {...alertsBellProps}
        />
      </div>

      {/* Tablet icon rail (md only) */}
      <IconRailSidebar
        user={user}
        activeUrl={activeUrl}
        items={items}
        onOpenDrawer={openDrawer}
        showAssistantButton={showAssistantButton}
        className="bg-app shadow-nav-edge fixed inset-y-0 left-0 z-30 hidden md:flex lg:hidden"
        {...alertsBellProps}
      />

      {/* Mobile (<md) top bar + drawer */}
      <MobileTopBar onOpenDrawer={openDrawer} className="md:hidden" {...alertsBellProps} />
      <MobileNavDrawer
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        user={user}
        activeUrl={activeUrl}
        items={items}
        footerItems={footerItems}
        showAccountCard={showAccountCard}
        showAssistantButton={showAssistantButton}
        onOpenSearch={openSearch}
        {...alertsBellProps}
      />

      <EntitySearchModal isOpen={isSearchOpen} onOpenChange={setIsSearchOpen} />
    </>
  );
};

/**
 * App-wired `MainSidebar` used by `HomeScene`. Pulls the authenticated user
 * and active route from context and composes the nav item list (dashboard,
 * accounts, spending, recurring) before delegating to `BaseSidebar`.
 *
 * Three consumer features ride on feature gates:
 * - "Bills & Subscriptions" entry — `feat-obligations`.
 * - "Mantle Assistant" footer button — `feat-assistant`. Rendered by each
 *   sidebar variant via `MantleAssistantNavButton`; the gate flag flows
 *   down through `BaseSidebar`.
 * - Alerts bell + unread pip — `feat-alerts-center`. Rendered by each sidebar
 *   variant and the mobile top bar via `NotificationBellButton`.
 *
 * The unread count is read here rather than inside the bell because all three
 * sidebar presentations mount at every viewport (inactive ones hidden by CSS),
 * so a self-fetching bell would open one query subscription per presentation.
 * It polls because the chrome never unmounts: without a cadence the count
 * would only move on a page load or a mutation the viewer performed
 * themselves.
 *
 * Internal debug utilities (Sentry test, Environment) live in the avatar
 * dropdown's `Debug` submenu gated on `debug-tools` ([ORN-691]).
 */
const MainSidebar = () => {
  const { user, loading } = useAuthState();
  const location = useLocation();
  const addDateParamsToPath = usePathWithDateRange();
  const activeUrl = location.pathname;
  const showBillsSubscriptions = useFeatureGate(FeatureGate.Obligations);
  const showAssistantButton = useFeatureGate(FeatureGate.Assistant);
  const showAlertsBell = useFeatureGate(FeatureGate.AlertsCenter);
  const { visible, showAlerts } = useSidePanel();
  const isXl = useBreakpoint('xl');

  // Filterless, like the Alerts Center's own counts read, so the two share one
  // cache entry and cannot report different numbers. The chrome never unmounts,
  // so without the poll the pip would freeze at its login-time number.
  const { counts: workItemCounts } = useWorkItemStatusCounts({
    skip: !showAlertsBell,
    pollInterval: ALERT_COUNT_POLL_INTERVAL_MS,
  });

  const alertsUnreadCount = workItemCounts.unread;

  if (loading) {
    return null;
  }

  if (!user) {
    return null;
  }

  const rawItems: NavItemType[] = [
    {
      label: 'Home',
      href: PATHS.DASHBOARD,
      icon: HomeIcon,
    },
    {
      label: 'Accounts',
      href: PATHS.ACCOUNTS,
      icon: BarChartIcon,
    },
    {
      label: 'Spending',
      href: PATHS.SPENDING(),
      icon: BankNoteIcon,
    },
    ...(showBillsSubscriptions
      ? [
          {
            label: 'Bills & Subscriptions',
            // The full label overflows the 64px icon rail and gets clipped
            // off-screen; the rail uses this shorter form instead. Full
            // sidebar + mobile drawer continue to show the long label.
            iconRailLabel: 'Bills',
            href: PATHS.OBLIGATIONS,
            icon: CalendarIcon,
          },
        ]
      : []),
    {
      label: 'Recurring',
      href: PATHS.RECURRING_TRANSACTIONS,
      icon: CalendarIcon,
    },
  ];

  const items: NavItemType[] = rawItems.map((item) => {
    if (item.items?.length) {
      return {
        ...item,
        href: item.href ? addDateParamsToPath(item.href) : undefined,
        items: item.items.map((subItem) => ({
          ...subItem,
          href: addDateParamsToPath(subItem.href),
        })),
      };
    }
    return {
      ...item,
      href: item.href ? addDateParamsToPath(item.href) : undefined,
    };
  });

  return (
    <BaseSidebar
      items={items}
      user={user}
      activeUrl={activeUrl}
      showAssistantButton={showAssistantButton}
      showAlertsBell={showAlertsBell}
      alertsUnreadCount={alertsUnreadCount}
      // `isXl` as well as the selection: the panel is the authority for "the
      // center is on screen" only where the panel exists. The selection outlives
      // a narrowing handoff, so below `xl` it would keep the bell marked current
      // long after the user had navigated off `/alerts` — where the pathname is
      // the authority instead, and already covers it.
      isAlertsPanelOpen={isXl && visible === 'alerts'}
      // Below `xl` there is no panel to open, so the bell stays a plain link
      // to the `/alerts` route.
      onOpenAlertsInPanel={isXl ? showAlerts : undefined}
    />
  );
};

export default MainSidebar;
